import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import wordmark from "../assets/badge.png";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#04070A] min-h-screen flex flex-col">
      <div className="noise-overlay" />
      <div className="p-6 md:p-10">
        <Link to="/" className="font-display font-semibold text-lg uppercase">
          Drift Tackle<span className="text-orange-500">.</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="w-full max-w-md"
        >
          <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Admin Access</span>
          <h1 className="font-display text-5xl uppercase tracking-tighter mt-4 leading-[0.9]">Log in</h1>

          <form onSubmit={submit} className="mt-12 flex flex-col gap-8" data-testid="login-form">
            <div>
              <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 py-3 focus:border-orange-500 focus:outline-none text-slate-100"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 py-3 focus:border-orange-500 focus:outline-none text-slate-100"
                data-testid="login-password"
              />
            </div>
            {error && <p className="text-red-400 text-sm font-mono-plex" data-testid="login-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 hover:bg-orange-600 active:scale-[0.98] transition-all disabled:opacity-50"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
